import React, { useState } from 'react';

import { Button, Input, Dropdown, Section } from '@components';

const FORMAT_TYPE_OPTIONS: OptionType<DynamicColumnTypeEnum>[] = [
  { label: 'Text', value: 'text' },
  { label: 'Phone', value: 'phone' },
  { label: 'Date', value: 'date' },
  { label: 'Enum', value: 'enum' },
  { label: 'Boolean', value: 'boolean' },
  { label: 'Composite', value: 'composite' },
];

type ColumnCompositeFieldsSectionProps = {
  fields?: Array<DynamicColumnCompositeSubFieldType> | null;
  onUpdate: (fields: Array<DynamicColumnCompositeSubFieldType> | null) => void;
};

const getNewField = (): DynamicColumnCompositeSubFieldType => ({
  id: crypto.randomUUID(),
  key: '',
});

const ColumnCompositeFieldsSection: React.FC<
  ColumnCompositeFieldsSectionProps
> = ({ fields, onUpdate }) => {
  const [editing, setEditing] = useState<string | number | null>(null);
  const [newField, setNewField] = useState(getNewField);

  const handleAddField = () => {
    if (!newField.key.trim()) return;

    const updated = [...(fields || []), newField];
    onUpdate(updated);
    setNewField(getNewField());
  };

  const handleUpdateField = (
    index: number,
    updates: Partial<DynamicColumnCompositeSubFieldType>
  ) => {
    const updated = fields?.map((f, i) =>
      i === index ? { ...f, ...updates } : f
    );
    onUpdate(updated || null);
  };

  const handleRemoveField = (index: number) => {
    const updated = fields?.filter((_, i) => i !== index);
    onUpdate(updated && updated.length > 0 ? updated : null);
  };

  const entries = fields || [];

  return (
    <Section
      title="Composite Fields"
      subtitle="Define sub-fields for composite column type"
    >
      <div className="flex flex-col gap-y-lg">
        {entries.length > 0 && (
          <div className="flex flex-col gap-md">
            {entries.map((field, index) => {
              const isEditing = editing === index || editing === field.id;
              return (
                <div
                  key={field.id || index}
                  className="flex flex-col gap-md p-lg bg-content rounded-sm border border-active"
                >
                  <div className="flex flex-row items-center justify-between">
                    <div>
                      <div className="text-small font-medium text-text">
                        {field.key}
                      </div>
                      {field.format && (
                        <div className="text-tiny text-textLight">
                          Format: {field.format}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-row gap-sm">
                      <Button
                        width="small"
                        type="primary"
                        icon={isEditing ? undefined : 'edit'}
                        onClick={() =>
                          setEditing(isEditing ? null : field.id || index)
                        }
                      >
                        {isEditing ? 'Done' : ''}
                      </Button>
                      <Button
                        width="small"
                        type="danger"
                        icon="delete"
                        onClick={() => handleRemoveField(index)}
                      />
                    </div>
                  </div>

                  {isEditing && (
                    <div className="flex flex-col gap-md border-t border-active pt-md">
                      <div className="flex flex-row gap-md">
                        <div className="flex-1">
                          <label className="text-small text-textLight font-normal mb-xs block">
                            Key *
                          </label>
                          <Input
                            value={field.key}
                            onChange={val =>
                              handleUpdateField(index, { key: val })
                            }
                            placeholder="field_key"
                            withBorder
                          />
                        </div>
                        <div className="flex-1">
                          <label className="text-small text-textLight font-normal mb-xs block">
                            Format
                          </label>
                          <Dropdown
                            value={field.format || 'text'}
                            options={FORMAT_TYPE_OPTIONS}
                            onChange={val =>
                              handleUpdateField(index, {
                                format: (val as DynamicColumnTypeEnum) || null,
                              })
                            }
                          />
                        </div>
                      </div>

                      {field.style && (
                        <div className="flex flex-col gap-md border-t border-active pt-md">
                          <h5 className="text-small font-medium text-textLight">
                            Field Style
                          </h5>
                          <div className="flex flex-row gap-md">
                            <div className="flex-1">
                              <label className="text-small text-textLight font-normal mb-xs block">
                                Color
                              </label>
                              <Input
                                value={field.style.color || ''}
                                onChange={val =>
                                  handleUpdateField(index, {
                                    style: {
                                      ...field.style,
                                      color: val || null,
                                    },
                                  })
                                }
                                placeholder="#000000"
                                withBorder
                              />
                            </div>
                            <div className="flex-1">
                              <label className="text-small text-textLight font-normal mb-xs block">
                                Font Size
                              </label>
                              <Input
                                value={field.style.fontSize || ''}
                                onChange={val =>
                                  handleUpdateField(index, {
                                    style: {
                                      ...field.style,
                                      fontSize: val || null,
                                    },
                                  })
                                }
                                placeholder="14px"
                                withBorder
                              />
                            </div>
                          </div>
                          <div className="flex flex-row gap-md">
                            <div className="flex-1">
                              <label className="text-small text-textLight font-normal mb-xs block">
                                Font Weight
                              </label>
                              <Input
                                value={field.style.fontWeight || ''}
                                onChange={val =>
                                  handleUpdateField(index, {
                                    style: {
                                      ...field.style,
                                      fontWeight: val || null,
                                    },
                                  })
                                }
                                placeholder="normal, bold"
                                withBorder
                              />
                            </div>
                            <div className="flex-1">
                              <label className="text-small text-textLight font-normal mb-xs block">
                                Background Color
                              </label>
                              <Input
                                value={field.style.backgroundColor || ''}
                                onChange={val =>
                                  handleUpdateField(index, {
                                    style: {
                                      ...field.style,
                                      backgroundColor: val || null,
                                    },
                                  })
                                }
                                placeholder="#ffffff"
                                withBorder
                              />
                            </div>
                          </div>
                        </div>
                      )}

                      <Button
                        width="small"
                        type="light"
                        onClick={() =>
                          handleUpdateField(index, {
                            style: field.style ? null : {},
                          })
                        }
                        className="mt-md"
                      >
                        {field.style ? 'Remove Style' : 'Add Style'}
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="flex flex-col gap-md p-lg bg-content rounded-sm border border-active">
          <h4 className="text-small font-medium text-text">Add New Field</h4>
          <div className="flex flex-col gap-md">
            <div className="flex flex-row gap-md">
              <div className="flex-1">
                <Input
                  value={newField.key}
                  onChange={val => setNewField({ ...newField, key: val })}
                  placeholder="field_key"
                  withBorder
                />
              </div>
              <div className="flex-1">
                <Dropdown
                  value={newField.format || 'text'}
                  options={FORMAT_TYPE_OPTIONS}
                  onChange={val =>
                    setNewField({
                      ...newField,
                      format: (val as DynamicColumnTypeEnum) || null,
                    })
                  }
                />
              </div>
            </div>
            <Button width="small" type="primary" onClick={handleAddField}>
              Add Field
            </Button>
          </div>
        </div>

        {entries.length === 0 && (
          <div className="text-center text-textLight text-small py-lg">
            No composite fields defined yet. Add one above.
          </div>
        )}
      </div>
    </Section>
  );
};

export default ColumnCompositeFieldsSection;
